import React from "react";

import { Box, CardContent, Typography, Avatar } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import ActionsList from "./ActionsList";
import InsightsModalManager, {
  ModalType,
} from "../../../../common/ModalManager";
import { toHHMMSS } from "../../../../../utils/formatTIme";
import { Quote as QuoteType } from "../../../services/episode.service";
import { Category } from "../../../../common/types";
import InsightCardShell from "./InsightCardShell";

export type QuoteObject = {
  speaker: string;
  quote: string;
  timestamp: string;
};

interface QuoteProps {
  quoteObject: QuoteObject;
  quotes: QuoteType[];
  refetch: () => void;
  category: Category;
}

export default function Quote({
  quoteObject,
  quotes,
  refetch,
  category,
}: QuoteProps) {
  const [openInsightsModalManager, setOpenInsightsModalManager] =
    React.useState(false);
  const handleOpenInsightsModalManager = () =>
    setOpenInsightsModalManager(true);
  const handleCopy = () => navigator.clipboard.writeText(quoteObject.quote);
  const speakerInitial = quoteObject.speaker?.[0]?.toUpperCase() || "?";

  return (
    <Box key={quoteObject.timestamp} sx={{ width: "100%" }}>
      <InsightsModalManager
        open={openInsightsModalManager}
        setOpen={setOpenInsightsModalManager}
        modalType={ModalType.QUOTES}
        modalTitle="Edit Quote"
        objectValue={quoteObject}
        values={quotes}
        refetch={refetch}
        category={category}
      />
      <InsightCardShell
        sx={{
          mb: 1.5,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Top Row: Avatar, Speaker, Timestamp, Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 36,
                  height: 36,
                  fontWeight: 700,
                  fontSize: 18,
                  mr: 1.5,
                }}
              >
                {speakerInitial}
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: 16 }}
              >
                {quoteObject.speaker}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
              <AccessTimeIcon
                sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500, mr: 1 }}
              >
                {toHHMMSS(quoteObject.timestamp)}
              </Typography>
              <ActionsList
                handleOpenInsightsModalManager={handleOpenInsightsModalManager}
                handleCopy={handleCopy}
              />
            </Box>
          </Box>
          {/* Quote Text */}
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              fontSize: 15,
              fontWeight: 400,
              lineHeight: 1.6,
              mt: 1.5,
            }}
          >
            {quoteObject.quote}
          </Typography>
        </CardContent>
      </InsightCardShell>
    </Box>
  );
}
